/**
 * Known series patterns for popular book series
 * Used for matching books to series when title parsing fails
 */

export interface KnownSeriesPattern {
  pattern: RegExp;
  seriesName: string;
  position: number;
  totalBooks?: number;
}

// Helper to create series entries more efficiently
function createSeriesEntries(
  seriesName: string,
  books: { pattern: RegExp; position: number }[],
  totalBooks?: number
): KnownSeriesPattern[] {
  return books.map(book => ({
    ...book,
    seriesName,
    totalBooks
  }));
}

export const KNOWN_SERIES_PATTERNS: KnownSeriesPattern[] = [
  // ========== FANTASY ==========
  
  // Harry Potter (7 books)
  ...createSeriesEntries("Harry Potter", [
    { pattern: /harry potter and the (philosopher'?s?|sorcerer'?s?) stone/i, position: 1 },
    { pattern: /harry potter and the chamber of secrets/i, position: 2 },
    { pattern: /harry potter and the prisoner of azkaban/i, position: 3 },
    { pattern: /harry potter and the goblet of fire/i, position: 4 },
    { pattern: /harry potter and the order of the phoenix/i, position: 5 },
    { pattern: /harry potter and the half[- ]blood prince/i, position: 6 },
    { pattern: /harry potter and the deathly hallows/i, position: 7 },
  ], 7),

  // A Song of Ice and Fire (5 books published)
  ...createSeriesEntries("A Song of Ice and Fire", [
    { pattern: /^a game of thrones$/i, position: 1 },
    { pattern: /^a clash of kings$/i, position: 2 },
    { pattern: /^a storm of swords$/i, position: 3 },
    { pattern: /^a feast for crows$/i, position: 4 },
    { pattern: /^a dance with dragons$/i, position: 5 },
  ], 5),

  // The Lord of the Rings (3 books)
  ...createSeriesEntries("The Lord of the Rings", [
    { pattern: /the fellowship of the ring/i, position: 1 },
    { pattern: /the two towers(?!.*babylon)/i, position: 2 },
    { pattern: /the return of the king/i, position: 3 },
  ], 3),

  // The Stormlight Archive (4+ books)
  ...createSeriesEntries("The Stormlight Archive", [
    { pattern: /the way of kings/i, position: 1 },
    { pattern: /words of radiance/i, position: 2 },
    { pattern: /oathbringer/i, position: 3 },
    { pattern: /rhythm of war/i, position: 4 },
    { pattern: /wind and truth/i, position: 5 },
  ], 10),

  // Mistborn Era 1 (3 books)
  ...createSeriesEntries("Mistborn", [
    { pattern: /the final empire/i, position: 1 },
    { pattern: /the well of ascension/i, position: 2 },
    { pattern: /the hero of ages/i, position: 3 },
  ], 3),

  // The Wheel of Time (14 books)
  ...createSeriesEntries("The Wheel of Time", [
    { pattern: /the eye of the world/i, position: 1 },
    { pattern: /the great hunt/i, position: 2 },
    { pattern: /the dragon reborn/i, position: 3 },
    { pattern: /the shadow rising/i, position: 4 },
    { pattern: /the fires of heaven/i, position: 5 },
    { pattern: /lord of chaos/i, position: 6 },
    { pattern: /a crown of swords/i, position: 7 },
    { pattern: /the path of daggers/i, position: 8 },
    { pattern: /winter'?s heart/i, position: 9 },
    { pattern: /crossroads of twilight/i, position: 10 },
    { pattern: /knife of dreams/i, position: 11 },
    { pattern: /the gathering storm/i, position: 12 },
    { pattern: /towers of midnight/i, position: 13 },
    { pattern: /a memory of light/i, position: 14 },
  ], 14),

  // The First Law (3 books)
  ...createSeriesEntries("The First Law", [
    { pattern: /the blade itself/i, position: 1 },
    { pattern: /before they are hanged/i, position: 2 },
    { pattern: /last argument of kings/i, position: 3 },
  ], 3),

  // The Kingkiller Chronicle (2+ books)
  ...createSeriesEntries("The Kingkiller Chronicle", [
    { pattern: /the name of the wind/i, position: 1 },
    { pattern: /the wise man'?s fear/i, position: 2 },
  ], 3),

  // A Court of Thorns and Roses (5 books)
  ...createSeriesEntries("A Court of Thorns and Roses", [
    { pattern: /a court of thorns and roses(?!.*(?:mist|wings|silver|frost))/i, position: 1 },
    { pattern: /a court of mist and fury/i, position: 2 },
    { pattern: /a court of wings and ruin/i, position: 3 },
    { pattern: /a court of frost and starlight/i, position: 4 },
    { pattern: /a court of silver flames/i, position: 5 },
  ], 5),

  // Throne of Glass (8 books)
  ...createSeriesEntries("Throne of Glass", [
    { pattern: /^throne of glass$/i, position: 1 },
    { pattern: /crown of midnight/i, position: 2 },
    { pattern: /heir of fire/i, position: 3 },
    { pattern: /queen of shadows/i, position: 4 },
    { pattern: /empire of storms/i, position: 5 },
    { pattern: /tower of dawn/i, position: 6 },
    { pattern: /kingdom of ash/i, position: 7 },
  ], 7),

  // Crescent City (3 books)
  ...createSeriesEntries("Crescent City", [
    { pattern: /house of earth and blood/i, position: 1 },
    { pattern: /house of sky and breath/i, position: 2 },
    { pattern: /house of flame and shadow/i, position: 3 },
  ], 3),

  // The Inheritance Cycle (4 books)
  ...createSeriesEntries("The Inheritance Cycle", [
    { pattern: /^eragon$/i, position: 1 },
    { pattern: /^eldest$/i, position: 2 },
    { pattern: /^brisingr$/i, position: 3 },
    { pattern: /^inheritance$/i, position: 4 },
  ], 4),

  // Lightbringer (5 books)
  ...createSeriesEntries("Lightbringer", [
    { pattern: /the black prism/i, position: 1 },
    { pattern: /the blinding knife/i, position: 2 },
    { pattern: /the broken eye/i, position: 3 },
    { pattern: /the blood mirror/i, position: 4 },
    { pattern: /the burning white/i, position: 5 },
  ], 5),

  // The Gentleman Bastard (3+ books)
  ...createSeriesEntries("Gentleman Bastard", [
    { pattern: /the lies of locke lamora/i, position: 1 },
    { pattern: /red seas under red skies/i, position: 2 },
    { pattern: /the republic of thieves/i, position: 3 },
  ], 3),

  // The Dresden Files (17+ books) - just the first several
  ...createSeriesEntries("The Dresden Files", [
    { pattern: /storm front/i, position: 1 },
    { pattern: /fool moon/i, position: 2 },
    { pattern: /grave peril/i, position: 3 },
    { pattern: /summer knight/i, position: 4 },
    { pattern: /death masks/i, position: 5 },
    { pattern: /blood rites/i, position: 6 },
    { pattern: /dead beat/i, position: 7 },
    { pattern: /proven guilty/i, position: 8 },
    { pattern: /white night/i, position: 9 },
    { pattern: /small favor/i, position: 10 },
  ], 17),

  // ========== YOUNG ADULT ==========

  // The Hunger Games (4 books)
  ...createSeriesEntries("The Hunger Games", [
    { pattern: /^the hunger games$/i, position: 1 },
    { pattern: /^catching fire$/i, position: 2 },
    { pattern: /^mockingjay$/i, position: 3 },
    { pattern: /the ballad of songbirds and snakes/i, position: 0.5 },
  ], 4),

  // Twilight (4 books)
  ...createSeriesEntries("Twilight", [
    { pattern: /^twilight$/i, position: 1 },
    { pattern: /^new moon$/i, position: 2 },
    { pattern: /^eclipse$/i, position: 3 },
    { pattern: /^breaking dawn$/i, position: 4 },
    { pattern: /^midnight sun$/i, position: 5 },
  ], 5),

  // Divergent (3 books)
  ...createSeriesEntries("Divergent", [
    { pattern: /^divergent$/i, position: 1 },
    { pattern: /^insurgent$/i, position: 2 },
    { pattern: /^allegiant$/i, position: 3 },
  ], 3),

  // The Maze Runner (5 books)
  ...createSeriesEntries("The Maze Runner", [
    { pattern: /^the maze runner$/i, position: 1 },
    { pattern: /^the scorch trials$/i, position: 2 },
    { pattern: /^the death cure$/i, position: 3 },
    { pattern: /^the kill order$/i, position: 0.5 },
    { pattern: /^the fever code$/i, position: 0.6 },
  ], 5),

  // Percy Jackson and the Olympians (5 books)
  ...createSeriesEntries("Percy Jackson and the Olympians", [
    { pattern: /the lightning thief/i, position: 1 },
    { pattern: /the sea of monsters/i, position: 2 },
    { pattern: /the titan'?s curse/i, position: 3 },
    { pattern: /the battle of the labyrinth/i, position: 4 },
    { pattern: /the last olympian/i, position: 5 },
  ], 5),

  // The Mortal Instruments (6 books)
  ...createSeriesEntries("The Mortal Instruments", [
    { pattern: /city of bones/i, position: 1 },
    { pattern: /city of ashes/i, position: 2 },
    { pattern: /city of glass/i, position: 3 },
    { pattern: /city of fallen angels/i, position: 4 },
    { pattern: /city of lost souls/i, position: 5 },
    { pattern: /city of heavenly fire/i, position: 6 },
  ], 6),

  // Shadow and Bone (Grisha Trilogy) (3 books)
  ...createSeriesEntries("Shadow and Bone", [
    { pattern: /^shadow and bone$/i, position: 1 },
    { pattern: /^siege and storm$/i, position: 2 },
    { pattern: /^ruin and rising$/i, position: 3 },
  ], 3),

  // Six of Crows (2 books)
  ...createSeriesEntries("Six of Crows", [
    { pattern: /^six of crows$/i, position: 1 },
    { pattern: /^crooked kingdom$/i, position: 2 },
  ], 2),

  // Red Queen (4 books)
  ...createSeriesEntries("Red Queen", [
    { pattern: /^red queen$/i, position: 1 },
    { pattern: /^glass sword$/i, position: 2 },
    { pattern: /^king'?s cage$/i, position: 3 },
    { pattern: /^war storm$/i, position: 4 },
  ], 4),

  // An Ember in the Ashes (4 books)
  ...createSeriesEntries("An Ember in the Ashes", [
    { pattern: /an ember in the ashes/i, position: 1 },
    { pattern: /a torch against the night/i, position: 2 },
    { pattern: /a reaper at the gates/i, position: 3 },
    { pattern: /a sky beyond the storm/i, position: 4 },
  ], 4),

  // ========== SCIENCE FICTION ==========

  // Dune (6 books by Frank Herbert)
  ...createSeriesEntries("Dune", [
    { pattern: /^dune$/i, position: 1 },
    { pattern: /^dune messiah$/i, position: 2 },
    { pattern: /^children of dune$/i, position: 3 },
    { pattern: /^god emperor of dune$/i, position: 4 },
    { pattern: /^heretics of dune$/i, position: 5 },
    { pattern: /^chapterhouse:?\s*dune$/i, position: 6 },
  ], 6),

  // Foundation (7 books)
  ...createSeriesEntries("Foundation", [
    { pattern: /^foundation$/i, position: 1 },
    { pattern: /^foundation and empire$/i, position: 2 },
    { pattern: /^second foundation$/i, position: 3 },
    { pattern: /^foundation'?s edge$/i, position: 4 },
    { pattern: /^foundation and earth$/i, position: 5 },
    { pattern: /^prelude to foundation$/i, position: 0.5 },
    { pattern: /^forward the foundation$/i, position: 0.6 },
  ], 7),

  // Red Rising (6 books)
  ...createSeriesEntries("Red Rising", [
    { pattern: /^red rising$/i, position: 1 },
    { pattern: /^golden son$/i, position: 2 },
    { pattern: /^morning star$/i, position: 3 },
    { pattern: /^iron gold$/i, position: 4 },
    { pattern: /^dark age$/i, position: 5 },
    { pattern: /^light bringer$/i, position: 6 },
  ], 6),

  // The Expanse (9 books)
  ...createSeriesEntries("The Expanse", [
    { pattern: /leviathan wakes/i, position: 1 },
    { pattern: /caliban'?s war/i, position: 2 },
    { pattern: /abaddon'?s gate/i, position: 3 },
    { pattern: /cibola burn/i, position: 4 },
    { pattern: /nemesis games/i, position: 5 },
    { pattern: /babylon'?s ashes/i, position: 6 },
    { pattern: /persepolis rising/i, position: 7 },
    { pattern: /tiamat'?s wrath/i, position: 8 },
    { pattern: /leviathan falls/i, position: 9 },
  ], 9),

  // Ender's Game (5 books main series)
  ...createSeriesEntries("Ender's Game", [
    { pattern: /^ender'?s game$/i, position: 1 },
    { pattern: /^speaker for the dead$/i, position: 2 },
    { pattern: /^xenocide$/i, position: 3 },
    { pattern: /^children of the mind$/i, position: 4 },
    { pattern: /^ender in exile$/i, position: 5 },
  ], 5),

  // The Hitchhiker's Guide to the Galaxy (5 books)
  ...createSeriesEntries("The Hitchhiker's Guide to the Galaxy", [
    { pattern: /the hitchhiker'?s guide to the galaxy/i, position: 1 },
    { pattern: /the restaurant at the end of the universe/i, position: 2 },
    { pattern: /life,? the universe,? and everything/i, position: 3 },
    { pattern: /so long,? and thanks for all the fish/i, position: 4 },
    { pattern: /mostly harmless/i, position: 5 },
  ], 5),

  // Project Hail Mary / The Martian (standalone but popular)
  { pattern: /^the martian$/i, seriesName: "Andy Weir", position: 1 },
  { pattern: /^project hail mary$/i, seriesName: "Andy Weir", position: 2 },

  // ========== ROMANCE ==========

  // Bridgertons (8 books)
  ...createSeriesEntries("Bridgerton", [
    { pattern: /the duke and i/i, position: 1 },
    { pattern: /the viscount who loved me/i, position: 2 },
    { pattern: /an offer from a gentleman/i, position: 3 },
    { pattern: /romancing mister bridgerton/i, position: 4 },
    { pattern: /to sir phillip,? with love/i, position: 5 },
    { pattern: /when he was wicked/i, position: 6 },
    { pattern: /it'?s in his kiss/i, position: 7 },
    { pattern: /on the way to the wedding/i, position: 8 },
  ], 8),

  // The Love Hypothesis / Love on the Brain
  ...createSeriesEntries("Ali Hazelwood STEM", [
    { pattern: /the love hypothesis/i, position: 1 },
    { pattern: /love on the brain/i, position: 2 },
    { pattern: /love,? theoretically/i, position: 3 },
  ]),

  // Twisted (4 books)
  ...createSeriesEntries("Twisted", [
    { pattern: /twisted love/i, position: 1 },
    { pattern: /twisted games/i, position: 2 },
    { pattern: /twisted hate/i, position: 3 },
    { pattern: /twisted lies/i, position: 4 },
  ], 4),

  // It Ends With Us / It Starts With Us
  ...createSeriesEntries("It Ends With Us", [
    { pattern: /it ends with us/i, position: 1 },
    { pattern: /it starts with us/i, position: 2 },
  ], 2),

  // ========== THRILLER/MYSTERY ==========

  // Jack Reacher (27+ books) - first several
  ...createSeriesEntries("Jack Reacher", [
    { pattern: /^killing floor$/i, position: 1 },
    { pattern: /^die trying$/i, position: 2 },
    { pattern: /^tripwire$/i, position: 3 },
    { pattern: /^running blind$/i, position: 4 },
    { pattern: /^echo burning$/i, position: 5 },
    { pattern: /^without fail$/i, position: 6 },
    { pattern: /^persuader$/i, position: 7 },
    { pattern: /^the enemy$/i, position: 8 },
    { pattern: /^one shot$/i, position: 9 },
    { pattern: /^the hard way$/i, position: 10 },
  ]),

  // Millennium Series (4+ books)
  ...createSeriesEntries("Millennium", [
    { pattern: /the girl with the dragon tattoo/i, position: 1 },
    { pattern: /the girl who played with fire/i, position: 2 },
    { pattern: /the girl who kicked the hornets'? nest/i, position: 3 },
    { pattern: /the girl in the spider'?s web/i, position: 4 },
    { pattern: /the girl who takes an eye for an eye/i, position: 5 },
    { pattern: /the girl who lived twice/i, position: 6 },
  ], 6),

  // Gone Girl / Dark Places (Gillian Flynn)
  { pattern: /^gone girl$/i, seriesName: "Gillian Flynn", position: 1 },
  { pattern: /^dark places$/i, seriesName: "Gillian Flynn", position: 2 },
  { pattern: /^sharp objects$/i, seriesName: "Gillian Flynn", position: 3 },

  // ========== HORROR ==========

  // Stephen King's Dark Tower (8 books)
  ...createSeriesEntries("The Dark Tower", [
    { pattern: /the gunslinger/i, position: 1 },
    { pattern: /the drawing of the three/i, position: 2 },
    { pattern: /the waste lands/i, position: 3 },
    { pattern: /wizard and glass/i, position: 4 },
    { pattern: /wolves of the calla/i, position: 5 },
    { pattern: /song of susannah/i, position: 6 },
    { pattern: /the dark tower(?!.*series)/i, position: 7 },
    { pattern: /the wind through the keyhole/i, position: 4.5 },
  ], 8),

  // ========== CLASSICS ==========

  // The Chronicles of Narnia (7 books)
  ...createSeriesEntries("The Chronicles of Narnia", [
    { pattern: /the lion,? the witch,? and the wardrobe/i, position: 2 },
    { pattern: /prince caspian/i, position: 4 },
    { pattern: /the voyage of the dawn treader/i, position: 5 },
    { pattern: /the silver chair/i, position: 6 },
    { pattern: /the horse and his boy/i, position: 3 },
    { pattern: /the magician'?s nephew/i, position: 1 },
    { pattern: /the last battle/i, position: 7 },
  ], 7),

  // Anne of Green Gables (8 books)
  ...createSeriesEntries("Anne of Green Gables", [
    { pattern: /anne of green gables/i, position: 1 },
    { pattern: /anne of avonlea/i, position: 2 },
    { pattern: /anne of the island/i, position: 3 },
    { pattern: /anne of windy poplars/i, position: 4 },
    { pattern: /anne'?s house of dreams/i, position: 5 },
    { pattern: /anne of ingleside/i, position: 6 },
    { pattern: /rainbow valley/i, position: 7 },
    { pattern: /rilla of ingleside/i, position: 8 },
  ], 8),

  // Little House (9 books)
  ...createSeriesEntries("Little House", [
    { pattern: /little house in the big woods/i, position: 1 },
    { pattern: /farmer boy/i, position: 2 },
    { pattern: /little house on the prairie/i, position: 3 },
    { pattern: /on the banks of plum creek/i, position: 4 },
    { pattern: /by the shores of silver lake/i, position: 5 },
    { pattern: /the long winter/i, position: 6 },
    { pattern: /little town on the prairie/i, position: 7 },
    { pattern: /these happy golden years/i, position: 8 },
    { pattern: /the first four years/i, position: 9 },
  ], 9),

  // ========== CONTEMPORARY ==========

  // Daisy Jones & The Six / Malibu Rising etc (Taylor Jenkins Reid)
  { pattern: /daisy jones & the six/i, seriesName: "Taylor Jenkins Reid", position: 1 },
  { pattern: /malibu rising/i, seriesName: "Taylor Jenkins Reid", position: 2 },
  { pattern: /the seven husbands of evelyn hugo/i, seriesName: "Taylor Jenkins Reid", position: 3 },

  // ========== ADDITIONAL POPULAR FANTASY ==========

  // The Poppy War (3 books)
  ...createSeriesEntries("The Poppy War", [
    { pattern: /^the poppy war$/i, position: 1 },
    { pattern: /^the dragon republic$/i, position: 2 },
    { pattern: /^the burning god$/i, position: 3 },
  ], 3),

  // Priory of the Orange Tree (2 books)
  ...createSeriesEntries("The Roots of Chaos", [
    { pattern: /the priory of the orange tree/i, position: 1 },
    { pattern: /a day of fallen night/i, position: 0.5 },
  ], 2),

  // Fourth Wing (2+ books)
  ...createSeriesEntries("The Empyrean", [
    { pattern: /^fourth wing$/i, position: 1 },
    { pattern: /^iron flame$/i, position: 2 },
    { pattern: /^onyx storm$/i, position: 3 },
  ], 5),

  // Divine Rivals (2 books)
  ...createSeriesEntries("Letters of Enchantment", [
    { pattern: /^divine rivals$/i, position: 1 },
    { pattern: /^ruthless vows$/i, position: 2 },
  ], 2),

  // From Blood and Ash (6 books)
  ...createSeriesEntries("Blood and Ash", [
    { pattern: /from blood and ash/i, position: 1 },
    { pattern: /a kingdom of flesh and fire/i, position: 2 },
    { pattern: /the crown of gilded bones/i, position: 3 },
    { pattern: /the war of two queens/i, position: 4 },
    { pattern: /a soul of ash and blood/i, position: 5 },
    { pattern: /a light in the flame/i, position: 6 },
  ], 6),

  // The Cruel Prince (3 books)
  ...createSeriesEntries("The Folk of the Air", [
    { pattern: /^the cruel prince$/i, position: 1 },
    { pattern: /^the wicked king$/i, position: 2 },
    { pattern: /^the queen of nothing$/i, position: 3 },
  ], 3),

  // Circe / Song of Achilles (Madeline Miller)
  { pattern: /^the song of achilles$/i, seriesName: "Madeline Miller", position: 1 },
  { pattern: /^circe$/i, seriesName: "Madeline Miller", position: 2 },

  // The Night Circus / The Starless Sea (Erin Morgenstern)
  { pattern: /^the night circus$/i, seriesName: "Erin Morgenstern", position: 1 },
  { pattern: /^the starless sea$/i, seriesName: "Erin Morgenstern", position: 2 },

  // Piranesi / Jonathan Strange & Mr Norrell (Susanna Clarke)
  { pattern: /jonathan strange & mr\.? norrell/i, seriesName: "Susanna Clarke", position: 1 },
  { pattern: /^piranesi$/i, seriesName: "Susanna Clarke", position: 2 },

  // ========== ROMANTASY ==========

  // The Plated Prisoner (5 books)
  ...createSeriesEntries("The Plated Prisoner", [
    { pattern: /^gild$/i, position: 1 },
    { pattern: /^glint$/i, position: 2 },
    { pattern: /^gleam$/i, position: 3 },
    { pattern: /^glow$/i, position: 4 },
    { pattern: /^gold$/i, position: 5 },
  ], 5),

  // Zodiac Academy (9 books)
  ...createSeriesEntries("Zodiac Academy", [
    { pattern: /zodiac academy.*awakening/i, position: 1 },
    { pattern: /zodiac academy.*ruthless fae/i, position: 2 },
    { pattern: /zodiac academy.*reckoning/i, position: 3 },
    { pattern: /zodiac academy.*shadow princess/i, position: 4 },
    { pattern: /zodiac academy.*cursed fates/i, position: 5 },
    { pattern: /zodiac academy.*fated throne/i, position: 6 },
    { pattern: /zodiac academy.*heartless sky/i, position: 7 },
    { pattern: /zodiac academy.*sorrow and starlight/i, position: 8 },
  ], 9),

  // Kings of Sin (4 books)
  ...createSeriesEntries("Kings of Sin", [
    { pattern: /king of wrath/i, position: 1 },
    { pattern: /king of pride/i, position: 2 },
    { pattern: /king of greed/i, position: 3 },
    { pattern: /king of sloth/i, position: 4 },
  ], 4),
];

/**
 * Detect series from known patterns
 */
export function detectFromKnownPatterns(title: string): { 
  seriesName: string; 
  position: number; 
  totalBooks?: number 
} | null {
  const normalizedTitle = title.trim();
  
  for (const pattern of KNOWN_SERIES_PATTERNS) {
    if (pattern.pattern.test(normalizedTitle)) {
      return {
        seriesName: pattern.seriesName,
        position: pattern.position,
        totalBooks: pattern.totalBooks
      };
    }
  }
  return null;
}
